import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Typography, Chip, Stack, IconButton, Tooltip, Skeleton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { useAppTheme } from '../../hooks/useAppTheme';
import { analyticsService, StrategyComparisonRow } from '../../services/analytics.service';

/**
 * Compact strip on the Strategies page showing total signal counts per engine
 * over the last N days. Click "Detailed" to jump to the full Analytics page.
 */
const StrategyComparisonStrip: React.FC = () => {
  const { C } = useAppTheme();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState<StrategyComparisonRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await analyticsService.getStrategyComparison(days);
      setRows(data?.strategies || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days]);

  // Aggregate by engine (rockband / dailyblast / sniper)
  const byEngine = rows.reduce<Record<string, { total: number; buys: number; shorts: number }>>((acc, r) => {
    const e = r.engine || 'unknown';
    if (!acc[e]) acc[e] = { total: 0, buys: 0, shorts: 0 };
    acc[e].total += r.total; acc[e].buys += r.buys; acc[e].shorts += r.shorts;
    return acc;
  }, {});
  const engineLabel: Record<string, string> = {
    rockband: 'RockBand', dailyblast: 'DailyBlast', sniper: 'Sniper', unknown: 'Other',
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={<Typography variant="subtitle1" fontWeight={700}>Engine signal volume</Typography>}
        subheader={<Typography variant="caption" color="text.secondary">Total signals emitted by each engine in the last {days} days.</Typography>}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={days}
              onChange={(_, v) => v && setDays(v)}
            >
              {[7, 14, 30].map(p => <ToggleButton key={p} value={p}>{p}d</ToggleButton>)}
            </ToggleButtonGroup>
            <Tooltip title="Reload">
              <IconButton size="small" onClick={load} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Detailed performance analytics">
              <IconButton size="small" onClick={() => navigate('/analytics')}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        sx={{ pb: 0 }}
      />
      <CardContent>
        {loading && rows.length === 0 ? (
          <Stack direction="row" spacing={1}>
            {[0, 1, 2].map(i => <Skeleton key={i} variant="rectangular" width={180} height={56} sx={{ borderRadius: 1 }} />)}
          </Stack>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No signals in this window. Run a scan or wait for a scheduled one to fire.</Typography>
        ) : (
          <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
            {Object.entries(byEngine)
              .sort((a, b) => b[1].total - a[1].total)
              .map(([engine, v]) => (
              <Box key={engine} sx={{
                px: 1.5, py: 1, borderRadius: 1.25,
                bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                minWidth: 170,
              }}>
                <Typography variant="caption" sx={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
                  {engineLabel[engine] || engine}
                </Typography>
                <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{v.total}</Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                  <Chip label={`BUY ${v.buys}`} size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: C.bullMuted, color: C.bull, fontWeight: 700 }} />
                  <Chip label={`SHORT ${v.shorts}`} size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: C.bearMuted, color: C.bear, fontWeight: 700 }} />
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default StrategyComparisonStrip;
